import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { ConditionalWrapper } from "../../common/conditional-wrapper";
import {
  TilesTeaserSchemeDefinition,
  TilesTeaserSchemeName,
  TilesTeaserTextColor
} from "./constants/tiles-teaser-types";
import { AspectRatio } from "../../common/types";
import { HEADLINE_SIZE_H4, HEADLINE_TAG_H4 } from "../headline/constants/headline-constants";
import { getFirstNestedChild } from "../../utils/dom-helper";
import pfx from "../../utils/style-class-prefix-helper";
import { TilesTeaserSchemeConfiguration } from "../../constants/colors";
import { TILES_TEASER_SCHEME_CONFIG, TILES_TEASER_SCHEME_MAP } from "./constants/tiles-teaser-constants";
import {
  TILES_TEASER_COLORS,
  TILES_TEASER_DEFAULT_HORIZONTAL_CONTENT_ALIGNMENT,
  TILES_TEASER_DEFAULT_VERTICAL_CONTENT_ALIGNMENT,
  TILES_TEASER_DEFAULT_VIOLATOR_HORIZONTAL_POSITION,
  TILES_TEASER_HORIZONTAL_CONTENT_ALIGNMENTS,
  TILES_TEASER_VERTICAL_CONTENT_ALIGNMENTS,
  TilesTeaserContentAlignment,
  TilesTeaserHorizontalContentAlignment,
  TilesTeaserVerticalContentAlignment,
  TilesTeaserViolatorHorizontalPosition
} from "../../constants/global-constants";
import { camelToDashCase, shortenString, stripHtml } from "../../utils/string-format";
import { ASPECTRATIO_1X1 } from "../../common/constants";
import { setCssVariable } from "../../utils/css-variables";
import { HeadlineTag } from "../headline/constants/headline-types";
import { useTheRightHtmlTagForHeadline } from "../../utils/headline-helper";
import {
  ContainerBreakpointComponent,
  initContainerBreakpointsDelegate
} from "../../common/container-breakpoint-component";

// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot image (optional) - Holds a single eon-ui-image element. Can be omitted
 * @slot headline - Holds a single eon-ui-headline element
 * @slot text - Holds a single eon-ui-text element
 * @slot link - Holds a single eon-ui-link element
 * @slot violator - Holds a single eon-ui-violator element
 */
@Component({
  tag: "eon-ui-tiles-teaser",
  styleUrl: "tiles-teaser.scss",
  assetsDirs: ["assets-tiles-teaser"],
  shadow: true
})
export class TilesTeaser
  implements
    ColorableComponent<TilesTeaserSchemeConfiguration<TilesTeaserSchemeDefinition>>,
    ContainerBreakpointComponent {
  // @InitLogging() sentryTransaction;
  schemeConfig: TilesTeaserSchemeConfiguration<TilesTeaserSchemeDefinition> = TILES_TEASER_SCHEME_CONFIG;
  schemeMap = TILES_TEASER_SCHEME_MAP;

  /**
   * Array that defines component specific container breakpoints.
   */
  containerBreakpoints = [1200, 800, 400];

  @Element() hostElement: HTMLEonUiTilesTeaserElement;
  private imageElement: HTMLEonUiImageElement;
  private headlineElement: HTMLEonUiHeadlineElement;
  private linkElement: HTMLEonUiLinkElement;
  private infoElement: HTMLEonUiInfoIconElement;
  private textElement: HTMLEonUiTextElement;
  private hasViolator: boolean;

  /**
   * copyCharacterLimit will limit the charaters of copy text to the given value
   * @disableControlInStorybook true
   * @example default: 160
   */
  private copyCharacterLimit: number = 160;

  @State() activeScheme: TilesTeaserSchemeDefinition;
  @State() linkHover: boolean;
  @State() allowHorizontalContentAlignment: boolean = false;
  @State() allowVerticalContentAlignment: boolean = false;

  private get hasImage(): boolean {
    return this.imageElement != null;
  }

  /**
   * Raised when the component has been loaded.
   */
  @Event() tilesTeaserLoad: EventEmitter;

  /**
   * The default (unhovered) text color of the teaser.
   */
  @Prop({ reflect: true }) textColor: TilesTeaserTextColor = TILES_TEASER_COLORS[0];

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * name of the eon-ui-pictogram inside the teaser (see eon-ui-pictogram)
   */
  @Prop() pictogram: string;

  /**
   * horizontal Alignment of the content-box.
   * @example left | center | right
   */
  // prettier-ignore
  @Prop() horizontalContentAlignment: TilesTeaserHorizontalContentAlignment = TILES_TEASER_DEFAULT_HORIZONTAL_CONTENT_ALIGNMENT;

  /**
   * vertical Alignment of the content-box.
   * @example top | center | bottom | distant
   */
  // prettier-ignore
  @Prop() verticalContentAlignment: TilesTeaserVerticalContentAlignment = TILES_TEASER_DEFAULT_VERTICAL_CONTENT_ALIGNMENT;

  /**
   * Alignment of the content-box.
   * @example left | center | distant
   */
  @Prop() contentAlignment: TilesTeaserContentAlignment;

  /**
   * The color that should be displayed as overlay when hovering over the Teaser.
   */
  @Prop({ attribute: "background-color", reflect: true }) scheme: TilesTeaserSchemeName;

  /**
   * Defines the aspect ratio.
   */
  @Prop({ mutable: true, reflect: true }) aspectRatio: AspectRatio = ASPECTRATIO_1X1;

  /**
   * Defines the aspect ratio for tablet breakpoint.
   */
  @Prop({ mutable: true, reflect: true }) aspectRatioTablet: AspectRatio;

  /**
   * Defines the aspect ratio for mobile breakpoint.
   */
  @Prop({ mutable: true, reflect: true }) aspectRatioMobile: AspectRatio;

  /**
   * Defines horizontal position of the violator.
   */
  // prettier-ignore
  @Prop({ mutable: true }) violatorHorizontalPosition: TilesTeaserViolatorHorizontalPosition = TILES_TEASER_DEFAULT_VIOLATOR_HORIZONTAL_POSITION;

  /**
   * This property is used for SEO-Optimisation.
   */
  @Prop() headlineHtmlTag: HeadlineTag;

  /**
   * Remove border radius completely to be able to use linked tiles teasers in teaser walls or grids without gutter.
   */
  @Prop() removeBorderRadius: boolean;

  initContainerBreakpoints(): void {
    initContainerBreakpointsDelegate(this.hostElement, this.containerBreakpoints);
  }

  componentWillLoad() {
    this.imageElement = getFirstNestedChild(this.hostElement, "eon-ui-image");
    this.hasViolator = !!getFirstNestedChild(this.hostElement, "eon-ui-violator");

    if (this.hasImage) {
      this.imageElement.setAttribute("position-y", "center");
      this.imageElement.setAttribute("position-x", "center");
      this.imageElement.setAttribute("width", "100%");
      this.imageElement.setAttribute("height", "100%");
      this.imageElement.removeAttribute("caption");
    }

    this.linkElement = getFirstNestedChild(this.hostElement, "eon-ui-link");
    this.infoElement = getFirstNestedChild(this.hostElement, "eon-ui-info-icon");
    this.textElement = getFirstNestedChild(this.hostElement, "eon-ui-text");

    if (!!this.textElement) {
      this.getTruncatedBody();
    }

    this.headlineElement = getFirstNestedChild(this.hostElement, "eon-ui-headline");
    this.headlineElement.setAttribute("size", HEADLINE_SIZE_H4);
    this.headlineElement.setAttribute(
      "headline-html-tag",
      useTheRightHtmlTagForHeadline(this.headlineHtmlTag, HEADLINE_TAG_H4)
    );
    this.alignHeadline();

    this.checkHorizontalContentAlignment();
    this.checkVerticalContentAlignment();
    this.applyScheme();
    this.initContainerBreakpoints();
    this.setAspectRatio();
    this.infoElement?.addEventListener("click", (e) => {
      e.preventDefault();
    });
  }

  componentDidLoad() {
    this.setCssVariables();
    this.applyTextColor();
    this.tilesTeaserLoad.emit();
  }

  @Watch("scheme")
  applyScheme(): void {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Watch("textColor")
  applyTextColor() {
    setCssVariable(this.hostElement, "--textColor", `var(--eon-${camelToDashCase(this.textColor)})`);
    this.infoElement?.setAttribute("scheme", this.textColor);
  }

  @Watch("contentAlignment")
  alignHeadline() {
    this.headlineElement.setAttribute("alignment", this.contentAlignment === "left" ? "left" : "center");
  }

  @Watch("horizontalContentAlignment")
  checkHorizontalContentAlignment() {
    this.allowHorizontalContentAlignment = (TILES_TEASER_HORIZONTAL_CONTENT_ALIGNMENTS as ReadonlyArray<string>).includes(
      this.horizontalContentAlignment
    );
    if (this.allowHorizontalContentAlignment && !this.contentAlignment)
      this.headlineElement.setAttribute("alignment", this.horizontalContentAlignment);
  }

  @Watch("verticalContentAlignment")
  checkVerticalContentAlignment() {
    this.allowVerticalContentAlignment = (TILES_TEASER_VERTICAL_CONTENT_ALIGNMENTS as ReadonlyArray<string>).includes(
      this.verticalContentAlignment
    );
  }

  private getTruncatedBody() {
    const bodyLength = this.textElement.innerHTML && stripHtml(this.textElement.innerHTML).length;
    if (bodyLength > this.copyCharacterLimit) {
      this.textElement.innerHTML = shortenString(this.textElement.innerHTML, this.copyCharacterLimit, "...");
    }
  }

  @Watch("aspectRatio")
  @Listen("resize", { target: "window" })
  private setAspectRatio() {
    if (this.imageElement) {
      this.imageElement.setAttribute("use-as-background", "true");

      if (!this.aspectRatio) {
        this.imageElement.removeAttribute("use-as-background");
      } else {
        this.imageElement.setAttribute("use-as-background", "true");
      }
    }
  }

  @Listen("mouseenter")
  onMouseEnter() {
    if (this.linkElement) this.linkElement.setAttribute("force-hover-style", String(true));
  }

  @Listen("mouseleave")
  onMouseLeave() {
    if (this.linkElement) this.linkElement.setAttribute("force-hover-style", String(false));
  }

  render() {
    return (
      <Host
        class={{
          "no-image": !this.hasImage,
          "no-link": !this.linkElement
        }}
      >
        <ConditionalWrapper
          condition={!!this.linkElement}
          wrapper={(children) => (
            <a
              class={pfx({ teaser: true, "no-radius": !!this.removeBorderRadius })}
              href={this.linkElement?.href || "#"}
              target={this.linkElement?.target}
              title={this.linkElement?.title}
            >
              {children}
            </a>
          )}
          defaultWrapper={(children) => <div class={pfx({ teaser: true, "no-radius": true })}>{children}</div>}
        >
          <div
            class={pfx({
              wrapper: true,
              "only-visible-on-hover": this.hasImage && !!this.scheme,
              "hover-overlay": this.linkElement && !(this.hasImage && !!this.scheme),
              [`${this.aspectRatio}`]: !!this.aspectRatio,
              [`tablet-${this.aspectRatioTablet}`]: !!this.aspectRatioTablet,
              [`mobile-${this.aspectRatioMobile}`]: !!this.aspectRatioMobile
            })}
          >
            {this.hasImage && <slot name="image"></slot>}
            <div class={pfx({ overlay: true, "color-shade": !!this.scheme })}></div>
            <div
              class={pfx({
                content: true,
                [`horizontal-content-alignment-${this.horizontalContentAlignment}`]:
                  this.allowHorizontalContentAlignment && !this.contentAlignment,
                [`vertical-content-alignment-${this.verticalContentAlignment}`]:
                  this.allowVerticalContentAlignment && !this.contentAlignment,
                [`content-alignment-${this.contentAlignment}`]: this.contentAlignment,
                "has-violator": this.hasViolator
              })}
            >
              {this.hasViolator && (
                <div
                  class={pfx({
                    "tiles-teaser-violator": true,
                    [this.violatorHorizontalPosition]: true
                  })}
                >
                  <slot name="violator"></slot>
                </div>
              )}

              <div
                class={pfx({
                  ["content-wrapper"]: true
                })}
              >
                <div
                  class={pfx({
                    ["header-wrapper"]: true
                  })}
                >
                  {this.pictogram && (
                    <div
                      class={pfx({
                        "pictogram-wrapper": true
                      })}
                    >
                      <eon-ui-pictogram name={this.pictogram}></eon-ui-pictogram>
                    </div>
                  )}
                  {/* Note that we can't use a eon-ui-background here as wrapper because slotted children can't access our ShadowDOM! */}
                  <div class={pfx("headline-wrapper")}>
                    <slot name="headline"></slot>
                  </div>
                  {!!this.textElement && <slot name="text"></slot>}
                </div>
                <div
                  class={pfx({
                    ["cta-wrapper"]: true
                  })}
                >
                  <slot name="link"></slot>
                  <slot name="info-icon"></slot>
                </div>
              </div>
            </div>
          </div>
        </ConditionalWrapper>
      </Host>
    );
  }
}
